import axios from "axios";
import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  WiCloud,
  WiHumidity,
  WiThermometer,
  WiBarometer,
  WiWindy,
} from "react-icons/wi";
import BASE_URL from "../../../config/apiConfig";
import "./FarmDetailPage.css";

import L from "leaflet";
import "leaflet-routing-machine";
import "leaflet/dist/leaflet.css";
import "leaflet-routing-machine/dist/leaflet-routing-machine.css";

import { Box, Typography, Grid, CircularProgress } from "@mui/material";
import LandscapeIcon from "@mui/icons-material/Landscape";
import SpaIcon from "@mui/icons-material/Spa";
import TerrainIcon from "@mui/icons-material/Terrain";
import OpacityIcon from "@mui/icons-material/Opacity";
import GrainIcon from "@mui/icons-material/Grain";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import FavoriteIcon from "@mui/icons-material/Favorite";

import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import WeatherCard from "./WeatherCard";
import { getUserEmail } from "../../../utils/auth";

import ReactMarkdown from "react-markdown";
import Cookies from "js-cookie";

const FarmDetailPage = () => {
  const { fieldId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { sowingDate, map_image_url } = location.state || {};

  const [farmDetails, setFarmDetails] = useState(null);
  const [cropName, setCropName] = useState("");
  const [fieldCoordinates, setFieldCoordinates] = useState([]);
  const [overlayType, setOverlayType] = useState("gradient"); // default to gradient
  const [sentinelRemarks, setSentinelRemarks] = useState(null);
  const [sentinelLoading, setSentinelLoading] = useState(false); // ← ADD this

  const mapRef = useRef(null);
  const overlayRef = useRef(null);
  const polygonRef = useRef(null);

  const fetchCropName = async (cropCode) => {
    try {
      const { data } = await axios.post(
        `${BASE_URL}/api/user/farm-management/get-all-crops`,
      );
      const crop = data.crops?.find((c) => c.crop_code === cropCode);
      setCropName(crop ? crop.crop_name : "Unknown Crop");
    } catch {
      setCropName("Unknown Crop");
    }
  };
  const fetchFarmDetails = async () => {
    try {
      const { data } = await axios.post(
        `${BASE_URL}/api/user/farm-management/fetch-farm-details`,
        { FieldID: fieldId },
      );

      if (data.success) {
        const field = data.field_data;
        setFarmDetails(field);
        setFieldCoordinates(data.coordinates);

        if (field?.crop_code) {
          await fetchCropName(field.crop_code);
          // ❌ Don't call fetchSentinelRemarks here
        }
      }
    } catch (error) {
      console.error("Farm details fetch error:", error);
    }
  };

  const fetchSentinelRemarks = async (fieldData, cropName) => {
    try {
      // setSentinelLoading(true);

      const response = await axios.post(
        `${BASE_URL}/api/user/farm-management/openai-sentinel-remarks`,
        {
          crop: cropName,
          ndvi: fieldData.ndvi,
          ndwi: fieldData.ndwi,
          evi: fieldData.evi,
          msi: fieldData.msi,
          chlorophyll: fieldData.chlorophyll,
          fieldId: fieldId,
        },
      );

      if (response.data.success) {
        setSentinelRemarks(response.data.remarks);
      } else {
        setSentinelRemarks("Could not generate remarks.");
      }
    } catch (error) {
      console.error("Failed to fetch GPT remarks:", error);
      setSentinelRemarks("Error occurred while generating remarks.");
    } finally {
      setSentinelLoading(false); // ← ADD this
    }
  };

  const handleUpdateReport = async () => {
    if (!farmDetails) return;

    setSentinelLoading(true);

    try {
      await fetchSentinelRemarks(farmDetails, cropName);
    } catch (error) {
      console.error("Update report error:", error);
    } finally {
      setSentinelLoading(false);
    }
  };

  useEffect(() => {
  let interval;

  const loadFarmDetails = async () => {
    try {
      const { data } = await axios.post(
        `${BASE_URL}/api/user/farm-management/fetch-farm-details`,
        {
          FieldID: fieldId,
        }
      );

      if (data.success) {
        const field = data.field_data;

        setFarmDetails(field);
        setFieldCoordinates(data.coordinates);

        if (field?.crop_code) {
          await fetchCropName(field.crop_code);
        }

        // =========================
        // STOP POLLING IF READY
        // =========================

        if (
          field?.recommendations ||
          field?.causes
        ) {
          clearInterval(interval);
        }
      }
    } catch (error) {
      console.error(
        "Farm details fetch error:",
        error
      );
    }
  };

  // Initial fetch
  loadFarmDetails();

  // =========================
  // POLLING EVERY 20 SECONDS
  // ONLY WHILE PROCESSING
  // =========================

  interval = setInterval(() => {
    if (
      !farmDetails?.recommendations &&
      !farmDetails?.causes
    ) {
      loadFarmDetails();
    }
  }, 20000);

  return () => clearInterval(interval);

}, [fieldId]);

  const handleGenerateReport = () => {
    window.open(`/user/reports/farmology_report?fieldId=${fieldId}`, "_blank");
  };

  useEffect(() => {
    // STOP if analysis not ready yet
    if (
      !farmDetails ||
      !fieldCoordinates.length ||
      !(farmDetails?.recommendations || farmDetails?.causes)
    ) {
      return;
    }

    const {
      field_name,
      latitude,
      longitude,
      ndvi_image_url,
      gradient_image_url,
      rvi_image_url,
      cloud_cover,
    } = farmDetails;

    const coords = fieldCoordinates.map((coord) => [
      coord.latitude,
      coord.longitude,
    ]);

    const bounds = L.latLngBounds(coords);
    const center = bounds.getCenter();

    const handleDirectionClick = (destLat, destLng) => {
      if (!navigator.geolocation) {
        alert("❌ Your browser does not support geolocation.");
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const originLat = position.coords.latitude;
          const originLng = position.coords.longitude;
          const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${originLat},${originLng}&destination=${destLat},${destLng}&travelmode=driving`;
          window.open(googleMapsUrl, "_blank");
        },
        (error) => {
          let message = "❌ Unable to access your location.";
          switch (error.code) {
            case error.PERMISSION_DENIED:
              message = "⚠️ Location permission denied.";
              break;
            case error.POSITION_UNAVAILABLE:
              message = "⚠️ Location unavailable.";
              break;
            case error.TIMEOUT:
              message = "⏳ Location request timed out.";
              break;
          }
          alert(message);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
      );
    };

    let map = mapRef.current;
    if (!map) {
      map = L.map("detail-map").setView(center, 16);
      mapRef.current = map;

      L.tileLayer("https://{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}", {
        subdomains: ["mt0", "mt1", "mt2", "mt3"],
        maxZoom: 20,
        attribution: "Map data © Google",
      }).addTo(map);

      const polygon = L.polygon(coords, {
        color: "black",
        weight: 3,
        fillOpacity: 0.1,
      }).addTo(map);

      map.fitBounds(bounds);

      coords.forEach(([lat, lng]) => {
        const plantIcon = L.divIcon({
          className: "plant-emoji-icon",
          html: "🌱",
          iconSize: [20, 20],
          iconAnchor: [10, 10],
        });

        L.marker([lat, lng], { icon: plantIcon }).addTo(map);
      });

      map.on("click", (e) => {
        const { lat, lng } = e.latlng;
        const btnId = `go-btn-${Date.now()}`;
        const popupContent = `
          <b>Coordinates:</b><br/>
          Lat: ${lat.toFixed(6)}<br/>
          Lng: ${lng.toFixed(6)}<br/><br/>
          <button id="${btnId}">📍 Open in Google Maps</button>
        `;
        L.popup().setLatLng(e.latlng).setContent(popupContent).openOn(map);

        setTimeout(() => {
          const btn = document.getElementById(btnId);
          if (btn) btn.onclick = () => handleDirectionClick(lat, lng);
        }, 100);
      });
    }

    if (overlayRef.current) overlayRef.current.remove();
    if (polygonRef.current) polygonRef.current.remove();

    // let imageUrl;
    // if (cloud_cover <= 40 && ndvi_image_url) {
    //   imageUrl = `${BASE_URL}${ndvi_image_url}`; // NDVI image
    // } else if (rvi_image_url) {
    //   imageUrl = `${BASE_URL}${rvi_image_url}`; // RVI image
    // } else {
    //   imageUrl = null; // fallback if images are missing
    // }


    // NDVI RVI Case
    let imageUrl;

    if (ndvi_image_url) {
      imageUrl = `${BASE_URL}${ndvi_image_url}`;
    } else {
      imageUrl = null;
    }

    // if (overlayType === "ndvi" && ndvi_image_url) {
    //   imageUrl = `${BASE_URL}${ndvi_image_url}`;
    // } else if (overlayType === "rvi" && rvi_image_url) {
    //   imageUrl = `${BASE_URL}${rvi_image_url}`;
    // }

    overlayRef.current = L.imageOverlay(imageUrl, bounds, {
      opacity: 0.7,
      interactive: true,
    }).addTo(map);

    const invisiblePolygon = L.polygon(coords, {
      color: "#00000000",
      weight: 0,
      fillOpacity: 0,
    }).addTo(map);

    invisiblePolygon.on("click", (e) => {
      const { lat, lng } = e.latlng;
      const btnId = `go-btn-${Date.now()}`;
      const popupContent = `
        <b>Overlay Click:</b><br/>
        Lat: ${lat.toFixed(6)}<br/>
        Lng: ${lng.toFixed(6)}<br/><br/>
        <button id="${btnId}">📍 See Direction</button>
      `;
      L.popup().setLatLng(e.latlng).setContent(popupContent).openOn(map);

      setTimeout(() => {
        const btn = document.getElementById(btnId);
        if (btn) btn.onclick = () => handleDirectionClick(lat, lng);
      }, 100);
    });

    polygonRef.current = invisiblePolygon;
  }, [farmDetails, fieldCoordinates, overlayType]);

  const isRviActive =
    farmDetails?.cloud_cover > 40 && farmDetails?.rvi_image_url;

  const pieDataRaw = isRviActive
    ? [
        {
          name: "Good Crop Health & Irrigation (RVI)",
          value: farmDetails?.rvi_good_crop ?? null,
          color: "#177317",
        },
        {
          name: "Crop Health Attention (RVI)",
          value: farmDetails?.rvi_health_attention ?? null,
          color: "#FFA500",
        },
        {
          name: "Irrigation Attention (RVI)",
          value: farmDetails?.rvi_irrigation_attention ?? null,
          color: "#800080",
        },
        {
          name: "Critical Crop Health & Irrigation (RVI)",
          value: farmDetails?.rvi_critical ?? null,
          color: "#FF0000",
        },
      ]
    : [
        {
          name: "Good Crop Health & Irrigation",
          value: farmDetails?.good_crop ?? null,
          color: "#177317",
        },
        {
          name: "Crop Health Attention",
          value: farmDetails?.health_attention ?? null,
          color: "#FFA500",
        },
        {
          name: "Irrigation Attention",
          value: farmDetails?.irrigation_attention ?? null,
          color: "#800080",
        },
        {
          name: "Critical Crop Health & Irrigation",
          value: farmDetails?.critical ?? null,
          color: "#FF0000",
        },
      ];

  // const pieDataRaw = [
  //   {
  //     name: "Good Crop Health & Irrigation",
  //     value: farmDetails?.good_crop ?? null,
  //     color: "#177317",
  //   },
  //   {
  //     name: "Crop Health Attention",
  //     value: farmDetails?.health_attention ?? null,
  //     color: "#FFA500",
  //   },
  //   {
  //     name: "Irrigation Attention",
  //     value: farmDetails?.irrigation_attention ?? null,
  //     color: "#800080",
  //   },
  //   {
  //     name: "Critical Crop Health & Irrigation",
  //     value: farmDetails?.critical ?? null,
  //     color: "#FF0000",
  //   },
  // ];

  // For pie chart: show only items with value > 0
  const pieDataForChart = pieDataRaw.filter(
    (item) => item.value != null && item.value > 0,
  );

  // Utility to add status & color logic
  const getHealthStatus = (value) => {
    if (value === null || value === undefined)
      return {
        text: "N/A",
        color: "#9e9e9e",
        gradient: "linear-gradient(135deg, #eeeeee, #bdbdbd)",
      };
    if (value >= 70)
      return {
        text: `${value.toFixed(0)}% (Healthy)`,
        color: "#2e7d32",
        gradient: "linear-gradient(135deg, #e8f5e9, #a5d6a7)",
      };
    if (value >= 40)
      return {
        text: `${value.toFixed(0)}% (Moderate)`,
        color: "#f9a825",
        gradient: "linear-gradient(135deg, #fffde7, #fff59d)",
      };
    return {
      text: `${value.toFixed(0)}% (Critical)`,
      color: "#c62828",
      gradient: "linear-gradient(135deg, #ffebee, #ef9a9a)",
    };
  };

  const getIndexStatus = (value, label) => {
    if (value === null || value === undefined)
      return {
        text: "N/A",
        color: "#9e9e9e",
        gradient: "linear-gradient(135deg, #eeeeee, #bdbdbd)",
      };
    let status = "Low";
    let color = "#ef6c00";
    let gradient = "linear-gradient(135deg, #fff3e0, #ffe0b2)";

    if (value >= 0.7) {
      status = "High";
      color = "#2e7d32";
      gradient = "linear-gradient(135deg, #e8f5e9, #a5d6a7)";
    } else if (value >= 0.4) {
      status = "Moderate";
      color = "#f9a825";
      gradient = "linear-gradient(135deg, #fffde7, #fff59d)";
    }

    return { text: `${value.toFixed(2)} (${status})`, color, gradient };
  };

  // 🔥 Decide values based on NDVI / RVI (same as pie chart)

  const healthValue = isRviActive
    ? farmDetails?.rvi_good_crop
    : farmDetails?.good_crop;

  const ndviValue = farmDetails?.ndvi; // NDVI stays same
  const eviValue = farmDetails?.evi; // EVI stays same

  const healthStatus = getHealthStatus(healthValue);
  const ndviStatus = getIndexStatus(ndviValue, "NDVI");
  const eviStatus = getIndexStatus(eviValue, "EVI");

  const fieldData = [
    {
      label: "Land Size",
      value: `${(farmDetails?.field_area || 0).toFixed(2)} hectares`,
      icon: <LandscapeIcon sx={{ fontSize: 30, color: "#2e7d32" }} />,
      gradient: "linear-gradient(135deg, #e0f2f1, #b2dfdb)",
      labelColor: "#212121",
    },
    {
      label: "Crop Type",
      value: cropName || "Unknown",
      icon: <SpaIcon sx={{ fontSize: 30, color: "#43a047" }} />,
      gradient: "linear-gradient(135deg, #e8f5e9, #c8e6c9)",
      labelColor: "#212121",
    },
    {
      label: "NDVI",
      value: ndviStatus.text,
      icon: <ShowChartIcon sx={{ fontSize: 30, color: ndviStatus.color }} />,
      gradient: ndviStatus.gradient,
      labelColor: "#212121",
    },
    {
      label: "EVI",
      value: eviStatus.text,
      icon: <TrendingUpIcon sx={{ fontSize: 30, color: eviStatus.color }} />,
      gradient: eviStatus.gradient,
      labelColor: "#212121",
    },
    {
      label: isRviActive ? "Crop Health (RVI)" : "Crop Health (NDVI)",
      value: healthStatus.text,
      icon: <FavoriteIcon sx={{ fontSize: 30, color: healthStatus.color }} />,
      gradient: healthStatus.gradient,
      labelColor: "#212121",
    },
  ];

  // Helper function (put it above your component or in utils)
  const formatDate = (dateInput) => {
    if (!dateInput) return "N/A";
    const date = new Date(dateInput);
    if (isNaN(date)) return "Invalid Date";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // ─── Processing guard: if farmDetails loaded but analysis not ready yet ───
  const isAnalysisReady = farmDetails?.recommendations || farmDetails?.causes;

  return (
    <div className="container">
      {/* Header */}
      <div className="header">
        <h2 className="title">Farm Details</h2>

        {farmDetails?.recommendations || farmDetails?.causes ? (
  <button
    className="get-report-button"
    onClick={handleGenerateReport}
    disabled={!farmDetails}
  >
    Get Report
  </button>
) : null}

        {/* <button
          className="update-report-button"
          onClick={handleUpdateReport}
          disabled={!farmDetails}
        >
          Update Report
        </button> */}
      </div>

      {/* ── Show processing screen when farmDetails loaded but analysis not ready ── */}
      {farmDetails && !isAnalysisReady ? (
        <Box
          sx={{
            minHeight: "60vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            px: 4,
            py: 8,
            background: "linear-gradient(135deg, #f0f4f8, #e8f5e9)",
            borderRadius: "20px",
            mt: 3,
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #a5d6a7, #66bb6a)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 3,
              boxShadow: "0 8px 24px rgba(46,125,50,0.2)",
            }}
          >
            <Typography sx={{ fontSize: "2.2rem" }}>🌿</Typography>
          </Box>

          <Typography
            variant="h5"
            fontWeight="700"
            sx={{ color: "#1b5e20", mb: 1.5 }}
          >
            Your Farm Analysis Is Being Processed
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: "#4a6741",
              maxWidth: 480,
              lineHeight: 1.8,
              mb: 3,
              fontSize: "1rem",
            }}
          >
            Our satellite intelligence engine is analysing your field data,
            vegetation indices, and crop health metrics. This typically takes a
            few minutes.
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              backgroundColor: "#ffffff",
              border: "1px solid #c8e6c9",
              borderRadius: "12px",
              px: 3,
              py: 1.5,
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              mb: 4,
            }}
          >
            <CircularProgress
              size={18}
              thickness={5}
              sx={{ color: "#43a047" }}
            />
            <Typography
              variant="body2"
              sx={{ color: "#2e7d32", fontWeight: 500 }}
            >
              Processing satellite imagery &amp; generating AI insights…
            </Typography>
          </Box>

          <Typography
            variant="caption"
            sx={{ color: "#888", fontSize: "0.82rem" }}
          >
            Please check back in a few minutes. You can refresh this page to see
            the latest results.
          </Typography>
        </Box>
      ) : (
        <>
          {/* Hero Section */}
          <Box
            sx={{
              background: "linear-gradient(120deg, #f0f4c3, #aed581)",
              py: 5,
              px: 4,
              textAlign: "center",
              color: "#2e7d32",
              borderTopLeftRadius: 24,
              borderTopRightRadius: 24,
              zIndex: 1,
              position: "relative",
            }}
          >
            <Typography variant="h5" fontWeight="bold">
              🌿 Smart Farm Dashboard
            </Typography>
            <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 500 }}>
              {/* <strong>Location:</strong> {farmDetails?.location || "N/A"} */}
            </Typography>
          </Box>

          {/* Field Info Cards */}
          <Grid
            container
            spacing={4}
            sx={{ mt: 3, mb: 4, zIndex: 2, position: "relative" }}
          >
            {fieldData.map((item, index) => (
              <Grid item xs={12} sm={6} md={2.4} key={index}>
                <Box
                  sx={{
                    p: 3,
                    borderRadius: "20px",
                    background: item.gradient,
                    boxShadow:
                      "0 4px 12px rgba(0, 0, 0, 0.1), inset 0 1px 2px rgba(255,255,255,0.15)",
                    textAlign: "center",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-6px) scale(1.03)",
                      boxShadow: "0 14px 28px rgba(0, 0, 0, 0.15)",
                    },
                  }}
                >
                  <Box sx={{ mb: 1 }}>{item.icon}</Box>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                      letterSpacing: "0.5px",
                      mb: 0.5,
                      textTransform: "uppercase",
                      fontSize: "0.85rem",
                    }}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ fontSize: "1.2rem" }}
                  >
                    {item.value}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Details Section */}
          {farmDetails ? (
            <>
              <div
                className="card"
                style={{ padding: "1.5rem", marginTop: "2rem" }}
              >
                <h3 className="section-title">Basic Information</h3>

                {/* <div className="overlay-toggle" style={{ marginBottom: "1rem" }}>
                  <button
                    className={overlayType === "gradient" ? "active" : ""}
                    onClick={() => setOverlayType("gradient")}
                  >
                    Gradient View
                  </button>
                  <button
                    className={overlayType === "ndvi" ? "active" : ""}
                    onClick={() => setOverlayType("ndvi")}
                  >
                    NDVI View
                  </button>
                  <button
                    className={overlayType === "rvi" ? "active" : ""}
                    onClick={() => setOverlayType("rvi")}
                  >
                    RVI View
                  </button>
                </div> */}

                <div
                  className="info-map-container"
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "2rem",
                  }}
                >
                  {/* Info */}
                  <div
                    className="info-section"
                    style={{
                      flex: "1 1 350px",
                      minWidth: "300px",
                    }}
                  >
                    <p className="info">
                      <strong>Farm Name:</strong> {farmDetails.field_name}
                    </p>
                    <p className="info">
                      <strong>Address:</strong> {farmDetails.address || "N/A"}
                    </p>
                    <p className="info">
                      <strong>Farm Area:</strong>{" "}
                      {(farmDetails.field_area || 0).toFixed(2)} hectares
                    </p>
                    <p className="info">
                      <strong>Crop Name:</strong> {cropName}
                    </p>
                    <p className="info">
                      <strong>Center Coordinates:</strong> Lat{" "}
                      {farmDetails.latitude.toFixed(6)}, Long{" "}
                      {farmDetails.longitude.toFixed(6)}
                    </p>
                    <p className="info">
                      <strong>
                        {cropName?.toLowerCase() === "tea"
                          ? "Pruning Date:"
                          : "Sowing Date:"}
                      </strong>{" "}
                      {formatDate(sowingDate)}
                    </p>
                    <p className="info">
                      <strong>Satellite Date:</strong>{" "}
                      {formatDate(farmDetails.satellite_date)}
                    </p>
                  </div>

                  {/* Map */}
                  <div
                    className="map-wrapper"
                    style={{
                      flex: "1 1 500px",
                      minHeight: "300px",
                      borderRadius: "12px",
                      overflow: "hidden",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                    }}
                  >
                    <div
                      id="detail-map"
                      className="map-view"
                      style={{
                        width: "100%",
                        height: "100%",
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Crop Condition Pie Chart */}
              <div
                className="card"
                style={{
                  padding: "1.5rem",
                  borderRadius: "1rem",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                  backgroundColor: "#fff",
                }}
              >
                <h3
                  className="section-title"
                  style={{
                    marginBottom: "1rem",
                    fontSize: "20px",
                    fontWeight: "600",
                    color: "#2c3e50",
                  }}
                >
                  Crop Condition Analysis
                </h3>

                <div
                  className="pie-chart-section"
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "2rem",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  {/* Description List */}
                  <div
                    className="pie-chart-description"
                    style={{
                      flex: "1 1 200px",
                      minWidth: "240px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "1rem",
                    }}
                  >
                    {pieDataRaw.map((item, index) => (
                      <div
                        key={index}
                        className="stat-item"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          background: "#f9f9f9",
                          padding: "1rem 1rem",
                          borderRadius: "8px",
                          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                        }}
                      >
                        <span
                          style={{
                            backgroundColor: item.color,
                            width: "14px",
                            height: "14px",
                            display: "inline-block",
                            borderRadius: "4px",
                            marginRight: "12px",
                            border: "1px solid #ccc",
                            flexShrink: 0,
                          }}
                        ></span>
                        <p
                          style={{
                            margin: 0,
                            fontSize: "14px",
                            lineHeight: "1.4",
                            color: item.value != null ? "#2c3e50" : "#999",
                            wordWrap: "break-word",
                          }}
                        >
                          <strong>{item.name}:</strong>{" "}
                          {item.value != null ? `${item.value}%` : "N/A"}
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* Pie Chart */}
                  <div
                    className="pie-chart-container"
                    style={
                      {
                        // flex: "1 1 300px",
                        // minWidth: "280px",
                        // padding: "1rem",
                        // borderRadius: "12px",
                        // backgroundColor: "#fafafa",
                        // boxShadow: "inset 0 0 8px rgba(0,0,0,0.03)",
                      }
                    }
                  >
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie
                          data={pieDataForChart}
                          dataKey="value"
                          outerRadius={80}
                          label={({ name, percent }) =>
                            `${name.split(" ")[0]}: ${(percent * 100).toFixed(0)}%`
                          }
                          labelLine={false}
                        >
                          {pieDataForChart.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value, name) => [`${value}%`, name]}
                          wrapperStyle={{
                            fontSize: "13px",
                            padding: "8px",
                            backgroundColor: "#ffffff",
                            border: "1px solid #ddd",
                            borderRadius: "8px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {sentinelLoading ? (
                <div className="card">
                  <h3 className="section-title">
                    📊 AI Sentinel Data Insights
                  </h3>
                  <div
                    style={{
                      backgroundColor: "#f9f9f9",
                      padding: "1.5rem",
                      borderRadius: "12px",
                      fontSize: "0.95rem",
                      lineHeight: 1.7,
                      textAlign: "center",
                    }}
                  >
                    <span>Updating insights...</span>
                    <div className="spinner" style={{ marginTop: "1rem" }} />
                  </div>
                </div>
              ) : farmDetails?.recommendations || farmDetails?.causes ? (
                <div className="card">
                  <h3 className="section-title">
                    📊 AI Sentinel Data Insights
                  </h3>

                  <div
                    className="insights-content"
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "2rem",
                      alignItems: "flex-start",
                    }}
                  >
                    {/* Image section */}
                    {farmDetails?.image_url && (
                      <div
                        style={{
                          flex: "1 1 320px",
                          maxWidth: "380px",
                          borderRadius: "12px",
                          overflow: "hidden",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                          backgroundColor: "#fff",
                        }}
                      >
                        <img
                          src={`${BASE_URL}/media/generated_images/${farmDetails.image_url}`}
                          alt="AI Crop Insight"
                          style={{
                            width: "100%",
                            display: "block",
                            borderRadius: "12px",
                          }}
                        />
                      </div>
                    )}

                    {/* Textual Insights */}
                    <div
                      style={{
                        flex: "2 1 400px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "1.5rem",
                        backgroundColor: "#f9f9f9",
                        padding: "1.5rem",
                        borderRadius: "12px",
                        boxShadow: "inset 0 0 8px rgba(0,0,0,0.03)",
                      }}
                    >
                      {/* Health Description */}
                      {sentinelRemarks && (
                        <div>
                          <h4
                            style={{ marginBottom: "0.5rem", color: "#2e7d32" }}
                          >
                            Crop Health Summary
                          </h4>
                          <ReactMarkdown>{sentinelRemarks}</ReactMarkdown>
                        </div>
                      )}

                      {/* Causes */}
                      {farmDetails?.causes && (
                        <div>
                          <h4
                            style={{ marginBottom: "0.5rem", color: "#c0392b" }}
                          >
                            Possible Causes
                          </h4>
                          <ReactMarkdown>{farmDetails.causes}</ReactMarkdown>
                        </div>
                      )}

                      {/* Recommendations */}
                      {farmDetails?.recommendations && (
                        <div>
                          <h4
                            style={{ marginBottom: "0.5rem", color: "#2980b9" }}
                          >
                            Recommendations & Precautions
                          </h4>
                          <ReactMarkdown>
                            {farmDetails.recommendations}
                          </ReactMarkdown>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : null}

              {/* Weather */}
              <div style={{ marginTop: "2rem" }}>
                <h3 className="section-title">
                  🌾 6-Day Farm Weather Forecast & Risk Alerts
                </h3>

                <WeatherCard
                  lat={farmDetails.latitude}
                  lon={farmDetails.longitude}
                />
              </div>
            </>
          ) : (
            <div className="loading">
              <div className="loader"></div>
              Loading farm details...
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FarmDetailPage;
